import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tooltip,
  Typography,
  Upload,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile } from 'antd/es/upload/interface';
import {
  AppstoreOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FilterOutlined,
  HolderOutlined,
  PlusOutlined,
  ReloadOutlined,
  ScissorOutlined,
  ShoppingCartOutlined,
  StarFilled,
  TagOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  adminService,
  type Product,
  type ProductCategoryOption,
  type ProductPayload,
  type ProductUpdatePayload,
} from '../../services/adminService';
import { handleApiError } from '../../services/api';
import { ProductService } from '../../services/productService';
import { MobileCardPreview, MobilePhotoCropper } from '../../components/admin/MobilePhotoTools';

const { Search, TextArea } = Input;
const { Title, Text } = Typography;

interface ProductStats {
  totalProducts: number;
  activeProducts: number;
  inactiveProducts: number;
  categories: { category: string; count: number }[];
}

interface ProductFormValues {
  SupersedeNo: string;
  UOM: string;
  Description: string;
  ProductDetail?: string;
  Category?: string;
  Price: number;
  ConvRate: number;
  NetContentLabel?: string;
  DeliveryCalUnit: number;
  Active: boolean;
}

type ProductFormMode = 'create' | 'edit';

const ProductManagement: React.FC = () => {
  const [form] = Form.useForm<ProductFormValues>();

  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<ProductCategoryOption[]>([]);
  const [stats, setStats] = useState<ProductStats | null>(null);

  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);

  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [sortBy, setSortBy] = useState<'SupersedeNo' | 'Description' | 'Price' | 'Category'>('SupersedeNo');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');

  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<ProductFormMode>('create');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [imageFileList, setImageFileList] = useState<UploadFile[]>([]);
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);

  // Reorder mode: drag-drop products to set their display order on the website.
  const [reorderMode, setReorderMode] = useState(false);
  const [reorderList, setReorderList] = useState<Product[]>([]);
  const [savingOrder, setSavingOrder] = useState(false);
  const dragIndex = useRef<number | null>(null);

  const enterReorderMode = async () => {
    try {
      setLoading(true);
      // Load the full catalog (unfiltered) in manual order so dragging sets a
      // meaningful global order. ~tens of products, so a single page is fine.
      const response = await adminService.getProducts({ page: 1, limit: 1000 });
      setReorderList(response.products);
      setReorderMode(true);
    } catch (error) {
      message.error(handleApiError(error) || 'Failed to load products for reordering');
    } finally {
      setLoading(false);
    }
  };

  const exitReorderMode = () => {
    setReorderMode(false);
    setReorderList([]);
    void loadProducts();
  };

  const persistOrder = async (list: Product[]) => {
    try {
      setSavingOrder(true);
      await adminService.reorderProducts(list.map((p) => p.SupersedeNo));
      message.success('Order saved');
    } catch (error) {
      message.error(handleApiError(error) || 'Failed to save order');
    } finally {
      setSavingOrder(false);
    }
  };

  const handleRowDrop = (targetIndex: number) => {
    const from = dragIndex.current;
    dragIndex.current = null;
    if (from === null || from === targetIndex) return;
    const next = [...reorderList];
    const [moved] = next.splice(from, 1);
    if (!moved) return;
    next.splice(targetIndex, 0, moved);
    setReorderList(next);
    void persistOrder(next);
  };

  const reorderColumns: ColumnsType<Product> = [
    {
      title: '',
      key: 'drag',
      width: 40,
      render: () => <HolderOutlined style={{ cursor: 'move', color: '#999' }} />,
    },
    {
      title: 'Image',
      key: 'image',
      width: 70,
      render: (_: unknown, record: Product) => (
        <Image
          width={44}
          height={44}
          style={{ objectFit: 'cover', borderRadius: 4 }}
          src={ProductService.getProductImageUrl(record.MainPictureFull)}
          preview={false}
          fallback="/images/product-placeholder.svg"
        />
      ),
    },
    { title: 'Code', dataIndex: 'SupersedeNo', key: 'SupersedeNo', width: 120 },
    { title: 'Description', dataIndex: 'Description', key: 'Description', ellipsis: true },
    {
      title: 'Category',
      key: 'Category',
      width: 160,
      render: (_: unknown, record: Product) => record.CategoryDescription || record.Category || '—',
    },
    {
      title: 'Published',
      key: 'Published',
      width: 110,
      render: (_: unknown, record: Product) =>
        record.Published ? <Badge status="success" text="Published" /> : <Badge status="default" text="Hidden" />,
    },
  ];

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminService.getProducts({
        page: currentPage,
        limit: pageSize,
        search: searchTerm || undefined,
        category: selectedCategory || undefined,
        status: selectedStatus,
        sortBy,
        sortOrder,
      });

      setProducts(response.products);
      setTotal(response.total);
    } catch (error) {
      console.error('Failed to load products:', error);
      message.error(handleApiError(error) || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, selectedCategory, selectedStatus, sortBy, sortOrder]);

  const loadCategories = useCallback(async () => {
    try {
      const categoryOptions = await adminService.getAllCategories();
      setCategories(categoryOptions);
    } catch (error) {
      console.error('Failed to load categories:', error);
      message.error(handleApiError(error) || 'Failed to load categories');
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const statsData = await adminService.getProductStats();
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load stats:', error);
      message.error(handleApiError(error) || 'Failed to load product statistics');
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    loadCategories();
    loadStats();
  }, [loadCategories, loadStats]);

  const resetFormState = () => {
    form.resetFields();
    setImageFileList([]);
    setPendingImageFile(null);
    setEditingProduct(null);
  };

  const refreshAfterMutation = async () => {
    await Promise.all([loadProducts(), loadStats()]);
  };

  const getCategoryLabel = (product: Product): string => {
    return product.CategoryDescription || product.Category || 'Uncategorised';
  };

  const getStatusBadge = (active?: string) => {
    return active === 'N'
      ? <Badge status="default" text="Inactive" />
      : <Badge status="success" text="Active" />;
  };

  const loadProductDetail = async (product: Product): Promise<Product> => {
    return adminService.getProductDetail(product.SupersedeNo, product.UOM);
  };

  const openCreateModal = () => {
    resetFormState();
    setFormMode('create');
    form.setFieldsValue({
      SupersedeNo: '',
      UOM: '',
      Description: '',
      ProductDetail: '',
      Category: undefined,
      Price: 0,
      ConvRate: 1,
      NetContentLabel: '',
      DeliveryCalUnit: 0,
      Active: true,
    });
    setFormModalOpen(true);
  };

  const openEditModal = async (product: Product) => {
    setDetailLoading(true);
    try {
      const detail = await loadProductDetail(product);
      resetFormState();
      setFormMode('edit');
      setEditingProduct(detail);
      form.setFieldsValue({
        SupersedeNo: detail.SupersedeNo,
        UOM: detail.UOM,
        Description: detail.Description,
        ProductDetail: detail.prodDetail || detail.ProductDetail || '',
        Category: detail.Category || undefined,
        Price: detail.Price,
        ConvRate: detail.ConvRate,
        NetContentLabel: detail.NetContentLabel || '',
        DeliveryCalUnit: detail.DeliveryCalUnit,
        Active: detail.Active !== 'N',
      });
      setFormModalOpen(true);
    } catch (error) {
      console.error('Failed to load product for editing:', error);
      message.error(handleApiError(error) || 'Failed to load product details');
    } finally {
      setDetailLoading(false);
    }
  };

  const openDetailModal = async (product: Product) => {
    setDetailModalOpen(true);
    setDetailLoading(true);
    try {
      const detail = await loadProductDetail(product);
      setSelectedProduct(detail);
    } catch (error) {
      console.error('Failed to load product detail:', error);
      setSelectedProduct(null);
      message.error(handleApiError(error) || 'Failed to load product details');
      setDetailModalOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedProduct(null);
  };

  const closeFormModal = () => {
    setFormModalOpen(false);
    resetFormState();
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value.trim());
    setCurrentPage(1);
  };

  const handleCategoryChange = (value?: string) => {
    setSelectedCategory(value || '');
    setCurrentPage(1);
  };

  const handleStatusChange = (value: 'all' | 'active' | 'inactive') => {
    setSelectedStatus(value);
    setCurrentPage(1);
  };

  const handleTableChange = (pagination: any, _filters: any, sorter: any) => {
    setCurrentPage(pagination.current || 1);
    setPageSize(pagination.pageSize || 20);

    const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    if (activeSorter?.columnKey) {
      setSortBy(activeSorter.columnKey as 'SupersedeNo' | 'Description' | 'Price' | 'Category');
      setSortOrder(activeSorter.order === 'descend' ? 'DESC' : 'ASC');
      return;
    }

    setSortBy('SupersedeNo');
    setSortOrder('ASC');
  };

  const handleToggleStatus = async (product: Product, active: boolean) => {
    try {
      await adminService.toggleProductStatus(product.SupersedeNo, active);
      message.success(`Product ${active ? 'activated' : 'deactivated'} successfully`);
      await refreshAfterMutation();
    } catch (error) {
      console.error('Failed to toggle product status:', error);
      message.error(handleApiError(error) || 'Failed to update product status');
    }
  };

  const handleTogglePublish = async (product: Product, published: boolean) => {
    try {
      await adminService.toggleProductPublish(product.SupersedeNo, product.UOM, published);
      message.success(
        `Product ${product.SupersedeNo}/${product.UOM} ${published ? 'published' : 'hidden'} successfully`
      );
      await refreshAfterMutation();
    } catch (error) {
      console.error('Failed to toggle publish status:', error);
      message.error(handleApiError(error) || 'Failed to update publish status');
    }
  };

  const handleImageListChange = ({ fileList }: { fileList: UploadFile[] }) => {
    const latestFileList = fileList.slice(-1);
    const nextFile = latestFileList[0]?.originFileObj;

    if (nextFile instanceof File) {
      const validationError = validateImageFile(nextFile);
      if (validationError) {
        message.error(validationError);
        setImageFileList([]);
        setPendingImageFile(null);
        return;
      }
    }

    setImageFileList(latestFileList);
    setPendingImageFile(nextFile instanceof File ? nextFile : null);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setFormSubmitting(true);

      const normalizedPayload: ProductPayload = {
        SupersedeNo: values.SupersedeNo.trim(),
        UOM: values.UOM.trim().toUpperCase(),
        Description: values.Description.trim(),
        ProductDetail: values.ProductDetail?.trim() || '',
        Category: values.Category || undefined,
        Price: Number(values.Price ?? 0),
        ConvRate: Number(values.ConvRate ?? 1),
        NetContentLabel: values.NetContentLabel?.trim() || '',
        DeliveryCalUnit: Number(values.DeliveryCalUnit ?? 0),
        Active: values.Active !== false,
      };

      if (formMode === 'create') {
        await adminService.createProduct(normalizedPayload);

        if (pendingImageFile) {
          await adminService.uploadProductImage(
            normalizedPayload.SupersedeNo,
            normalizedPayload.UOM,
            pendingImageFile
          );
        }

        message.success(`Product ${normalizedPayload.SupersedeNo} created successfully`);
      } else if (editingProduct) {
        const updatePayload: ProductUpdatePayload = {
          Description: normalizedPayload.Description,
          ProductDetail: normalizedPayload.ProductDetail,
          Category: normalizedPayload.Category,
          Price: normalizedPayload.Price,
          ConvRate: normalizedPayload.ConvRate,
          NetContentLabel: normalizedPayload.NetContentLabel ?? '',
          DeliveryCalUnit: normalizedPayload.DeliveryCalUnit,
          Active: normalizedPayload.Active,
        };
        if (normalizedPayload.UOM && normalizedPayload.UOM !== editingProduct.UOM) {
          updatePayload.UOM = normalizedPayload.UOM;
        }

        await adminService.updateProduct(editingProduct.SupersedeNo, editingProduct.UOM, updatePayload);

        if (pendingImageFile) {
          await adminService.uploadProductImage(
            editingProduct.SupersedeNo,
            editingProduct.UOM,
            pendingImageFile
          );
        }

        message.success(`Product ${editingProduct.SupersedeNo} updated successfully`);
      }

      closeFormModal();
      await refreshAfterMutation();
    } catch (error) {
      if (typeof error === 'object' && error !== null && 'errorFields' in error) {
        return;
      }

      console.error('Failed to save product:', error);
      message.error(handleApiError(error) || 'Failed to save product');
    } finally {
      setFormSubmitting(false);
    }
  };

  const columns: ColumnsType<Product> = [
    {
      title: 'Image',
      dataIndex: 'MainPictureName',
      key: 'image',
      width: 84,
      responsive: ['md'],
      render: (_imageName: string, record: Product) => (
        <div
          style={{
            width: 60,
            height: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            borderRadius: 4,
          }}
        >
          <Image
            src={ProductService.getProductImageUrl(record.MainPictureFull || record.MainPictureName)}
            alt={record.Description}
            width={60}
            height={60}
            style={{ objectFit: 'cover', borderRadius: 4 }}
            fallback="/images/product-placeholder.svg"
          />
        </div>
      ),
    },
    {
      title: 'Product Code',
      dataIndex: 'SupersedeNo',
      key: 'SupersedeNo',
      width: 120,
      sorter: true,
      render: (text: string, record: Product) => (
        <div>
          <Text strong>{text}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.UOM}
          </Text>
        </div>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'Description',
      key: 'Description',
      sorter: true,
      ellipsis: { showTitle: false },
      render: (text: string) => (
        <Tooltip title={text}>
          <Text style={{ maxWidth: 220 }}>{text}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Category',
      key: 'Category',
      width: 180,
      sorter: true,
      responsive: ['lg'],
      render: (_: unknown, record: Product) => getCategoryLabel(record),
    },
    {
      title: 'Price',
      dataIndex: 'Price',
      key: 'Price',
      width: 110,
      sorter: true,
      align: 'right',
      render: (price: number) => <Text strong>${price.toFixed(2)}</Text>,
    },
    {
      title: 'Net Content',
      key: 'NetContent',
      width: 130,
      align: 'center',
      responsive: ['xl'],
      render: (_: unknown, record: Product) =>
        record.NetContentLabel?.trim() || record.ConvRate.toFixed(2),
    },
    {
      title: 'Min Order Qty',
      dataIndex: 'DeliveryCalUnit',
      key: 'DeliveryCalUnit',
      width: 120,
      align: 'center',
      responsive: ['xl'],
      render: (value: number) => value.toFixed(0),
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      align: 'center',
      render: (_: unknown, record: Product) => getStatusBadge(record.Active),
    },
    {
      title: 'Published',
      key: 'published',
      width: 110,
      align: 'center',
      render: (_: unknown, record: Product) => (
        <Tooltip
          title={
            record.Published
              ? 'Visible on the public storefront. Click to hide.'
              : 'Hidden from the storefront. Click to publish.'
          }
        >
          <Switch
            size="small"
            checked={Boolean(record.Published)}
            onChange={(checked) => handleTogglePublish(record, checked)}
            checkedChildren="Public"
            unCheckedChildren="Hidden"
          />
        </Tooltip>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      fixed: 'right',
      render: (_: unknown, record: Product) => (
        <Space size="small" wrap>
          <Tooltip title="View details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => openDetailModal(record)}
            />
          </Tooltip>
          <Tooltip title="Edit product">
            <Button
              type="text"
              icon={<EditOutlined />}
              size="small"
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Tooltip title={record.Active === 'Y' ? 'Deactivate product' : 'Activate product'}>
            <Switch
              size="small"
              checked={record.Active !== 'N'}
              onChange={(checked) => handleToggleStatus(record, checked)}
              checkedChildren="On"
              unCheckedChildren="Off"
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2}>Product Management</Title>
        </Col>
        <Col>
          <Space>
            {reorderMode ? (
              <Button type="primary" onClick={exitReorderMode}>
                Done
              </Button>
            ) : (
              <>
                <Button icon={<HolderOutlined />} onClick={() => void enterReorderMode()}>
                  Reorder
                </Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
                  Add Product
                </Button>
              </>
            )}
          </Space>
        </Col>
      </Row>

      {stats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic title="Total Products" value={stats.totalProducts} prefix={<AppstoreOutlined />} />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Active Products"
                value={stats.activeProducts}
                prefix={<ShoppingCartOutlined />}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Inactive Products"
                value={stats.inactiveProducts}
                prefix={<TagOutlined />}
                valueStyle={{ color: '#cf1322' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic title="Categories" value={stats.categories.length} prefix={<FilterOutlined />} />
            </Card>
          </Col>
        </Row>
      )}

      {reorderMode && (
        <Card>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Drag rows to set the order products appear on the website. The top item shows first. Changes save automatically."
          />
          <Table
            columns={reorderColumns}
            dataSource={reorderList}
            rowKey="SupersedeNo"
            loading={loading || savingOrder}
            pagination={false}
            scroll={{ x: 700 }}
            onRow={(_, index) => ({
              draggable: true,
              onDragStart: () => {
                dragIndex.current = index ?? null;
              },
              onDragOver: (e) => e.preventDefault(),
              onDrop: () => handleRowDrop(index ?? 0),
              style: { cursor: 'move' },
            })}
          />
        </Card>
      )}

      {!reorderMode && (
      <>
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={8}>
            <Search
              placeholder="Search products..."
              allowClear
              onSearch={handleSearch}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              allowClear
              showSearch
              placeholder="Category"
              style={{ width: '100%' }}
              value={selectedCategory || undefined}
              onChange={handleCategoryChange}
              optionFilterProp="label"
              options={categories.map((category) => ({
                value: category.Category,
                label: category.CategoryDescription || category.Category,
              }))}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              style={{ width: '100%' }}
              value={selectedStatus}
              onChange={handleStatusChange}
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
              ]}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Space wrap>
              <Button icon={<ReloadOutlined />} onClick={() => { void loadProducts(); void loadStats(); }}>
                Refresh
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card>
        <style>
          {`
            .inactive-product-row {
              opacity: 0.6;
              background-color: #fafafa !important;
            }
            .inactive-product-row:hover > td {
              background-color: #f0f0f0 !important;
            }
          `}
        </style>
        <Table
          columns={columns}
          dataSource={products}
          rowKey={(record) => `${record.SupersedeNo}-${record.UOM}`}
          loading={loading}
          rowClassName={(record) => (record.Active === 'N' ? 'inactive-product-row' : '')}
          pagination={{
            current: currentPage,
            pageSize,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (rangeTotal, range) => `${range[0]}-${range[1]} of ${rangeTotal} products`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1400 }}
        />
      </Card>
      </>
      )}

      <Modal
        title="Product Details"
        open={detailModalOpen}
        onCancel={closeDetailModal}
        footer={[
          selectedProduct ? (
            <Button
              key="edit"
              icon={<EditOutlined />}
              onClick={() => {
                closeDetailModal();
                void openEditModal(selectedProduct);
              }}
            >
              Edit
            </Button>
          ) : null,
          <Button key="close" onClick={closeDetailModal}>
            Close
          </Button>,
        ]}
        width={700}
      >
        {selectedProduct && !detailLoading ? (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <Image
                src={ProductService.getProductImageUrl(selectedProduct.MainPictureFull || selectedProduct.MainPictureName)}
                alt={selectedProduct.Description}
                width={220}
                height={220}
                style={{ objectFit: 'cover', borderRadius: 8 }}
                fallback="/images/product-placeholder.svg"
              />
            </div>

            <Descriptions column={1} bordered>
              <Descriptions.Item label="Product Code">{selectedProduct.SupersedeNo}</Descriptions.Item>
              <Descriptions.Item label="UOM">{selectedProduct.UOM}</Descriptions.Item>
              <Descriptions.Item label="Description">{selectedProduct.Description}</Descriptions.Item>
              <Descriptions.Item label="Category">{getCategoryLabel(selectedProduct)}</Descriptions.Item>
              <Descriptions.Item label="Price">${selectedProduct.Price.toFixed(2)}</Descriptions.Item>
              <Descriptions.Item label="Net Content">
                {selectedProduct.NetContentLabel?.trim() || selectedProduct.ConvRate.toFixed(2)}
              </Descriptions.Item>
              <Descriptions.Item label="Minimum Order Quantity">
                {selectedProduct.DeliveryCalUnit.toFixed(0)}
              </Descriptions.Item>
              <Descriptions.Item label="Status">{getStatusBadge(selectedProduct.Active)}</Descriptions.Item>
            </Descriptions>

            {selectedProduct.prodDetail && (
              <div style={{ marginTop: 16 }}>
                <Title level={5}>Product Details</Title>
                <Text>{selectedProduct.prodDetail}</Text>
              </div>
            )}
          </div>
        ) : null}
      </Modal>

      <Modal
        title={formMode === 'create' ? 'Add Product' : 'Edit Product'}
        open={formModalOpen}
        onCancel={closeFormModal}
        onOk={() => { void handleSubmit(); }}
        confirmLoading={formSubmitting}
        okText={formMode === 'create' ? 'Create Product' : 'Save Changes'}
        width={760}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            Price: 0,
            ConvRate: 1,
            DeliveryCalUnit: 0,
            Active: true,
          }}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="SupersedeNo"
                label="Product Code"
                extra={
                  formMode === 'edit'
                    ? 'Product Code is part of the product identifier and cannot be changed after creation. Create a new product to use a different code.'
                    : undefined
                }
                rules={[
                  { required: true, message: 'Please enter a product code' },
                  { max: 20, message: 'Product code must be 20 characters or less' },
                  {
                    pattern: /^[a-zA-Z0-9_\- ]+$/,
                    message: 'Only letters, digits, spaces, underscores, and hyphens',
                  },
                ]}
              >
                <Input disabled={formMode === 'edit'} maxLength={20} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="UOM"
                label="UOM"
                extra={
                  formMode === 'edit'
                    ? 'Renaming UOM updates this product variant. Historical orders keep the UOM the customer originally ordered.'
                    : 'Short abbreviation, max 5 characters (e.g. PKT, KG, TRAY, QTR)'
                }
                rules={[
                  { required: true, message: 'Please enter a UOM' },
                  { max: 5, message: 'UOM must be 5 characters or less (e.g. "QTR" instead of "QUARTER")' },
                  {
                    pattern: /^[a-zA-Z0-9_\- ]+$/,
                    message: 'Only letters, digits, spaces, underscores, and hyphens',
                  },
                ]}
              >
                <Input maxLength={5} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="Description"
            label="Description"
            rules={[
              { required: true, message: 'Please enter a description' },
              { max: 255, message: 'Description must be 255 characters or less' },
            ]}
          >
            <Input maxLength={255} showCount />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="Category"
                label="Category"
                rules={[{ required: true, message: 'Please select a category' }]}
              >
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={categories.map((category) => ({
                    value: category.Category,
                    label: category.CategoryDescription || category.Category,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="Active"
                label="Active"
                valuePropName="checked"
                help="A UOM appears on the storefront when Published=Yes (toggle in the table), Active=Yes, and Price > 0."
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                name="Price"
                label="Price"
                rules={[{ required: true, message: 'Please enter a price' }]}
              >
                <InputNumber min={0} step={0.01} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="NetContentLabel"
                label="Net Content"
                extra="Free text shown to customers (e.g. '10 PCS', '12 eggs', '1 kg'). Leave blank to show the numeric value below instead."
                rules={[{ max: 50, message: 'Net Content must be 50 characters or less' }]}
              >
                <Input maxLength={50} placeholder="e.g. 10 PCS" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="DeliveryCalUnit"
                label="Minimum Order Quantity"
                rules={[{ required: true, message: 'Please enter minimum order quantity' }]}
              >
                <InputNumber min={0} step={1} precision={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                name="ConvRate"
                label="Conversion Rate"
                extra="Numeric multiplier used for internal delivery calculations. Defaults to 1; only change if you understand the legacy delivery math."
                rules={[{ required: true, message: 'Please enter a conversion rate' }]}
              >
                <InputNumber min={0} step={0.01} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="ProductDetail"
            label="Product Details"
            rules={[{ max: 1000, message: 'Product Details must be 1000 characters or less' }]}
          >
            <TextArea rows={4} placeholder="Optional longer product description" maxLength={1000} showCount />
          </Form.Item>

          {formMode === 'edit' && editingProduct ? (
            <>
              <ProductImageGallery
                key={`${editingProduct.SupersedeNo}|${editingProduct.UOM}`}
                supersedeNo={editingProduct.SupersedeNo}
                uom={editingProduct.UOM}
              />
              <MobileProductPhoto
                key={`mobile:${editingProduct.SupersedeNo}|${editingProduct.UOM}`}
                supersedeNo={editingProduct.SupersedeNo}
                uom={editingProduct.UOM}
                product={editingProduct}
              />
            </>
          ) : (
            <Form.Item label="Product Image">
              <Upload
                accept=".jpg,.jpeg,.png"
                beforeUpload={() => false}
                fileList={imageFileList}
                maxCount={1}
                onChange={handleImageListChange}
                onRemove={() => {
                  setImageFileList([]);
                  setPendingImageFile(null);
                }}
                listType="text"
              >
                <Tooltip title={IMAGE_REQUIREMENTS_TEXT}>
                  <Button icon={<UploadOutlined />}>Select Image</Button>
                </Tooltip>
              </Upload>
              <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                {IMAGE_REQUIREMENTS_TEXT} After the product is created you can add up to {MAX_IMAGES} images.
              </Text>
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

interface ProductImage {
  KeyID: number;
  SupersedeNo: string;
  UOM: string;
  DocName: string;
  SeqNo: number;
  path: string;
}

const MAX_IMAGES = 5;
const MAX_IMAGE_BYTES = 1 * 1024 * 1024; // 1 MB — must match backend Multer limit
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png'];
const IMAGE_REQUIREMENTS_TEXT =
  'JPEG or PNG only, max 1 MB per image. Up to 5 images per product.';
const MOBILE_IMAGE_REQUIREMENTS_TEXT =
  'JPEG or PNG only, max 1 MB. One mobile photo per product — uploading again replaces it.';

function validateImageFile(file: File): string | null {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return `"${file.name}" is not a JPEG or PNG image. Only .jpg, .jpeg, .png files are allowed.`;
  }
  if (file.size > MAX_IMAGE_BYTES) {
    const mb = (file.size / (1024 * 1024)).toFixed(2);
    return `"${file.name}" is ${mb} MB. Maximum allowed size is 1 MB per image.`;
  }
  return null;
}

interface ProductImageGalleryProps {
  supersedeNo: string;
  uom: string;
}

const ProductImageGallery: React.FC<ProductImageGalleryProps> = ({ supersedeNo, uom }) => {
  const [images, setImages] = useState<ProductImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  // Bumped on every successful mutation so cached image URLs get re-fetched.
  // Swapping/deleting keeps the same disk filenames; without this query param
  // the browser would keep serving stale content from cache.
  const [cacheBuster, setCacheBuster] = useState<number>(() => Date.now());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await adminService.listProductImages(supersedeNo, uom);
      setImages(list);
      setCacheBuster(Date.now());
    } catch (error) {
      message.error(handleApiError(error) || 'Failed to load product images');
    } finally {
      setLoading(false);
    }
  }, [supersedeNo, uom]);

  useEffect(() => {
    load();
  }, [load]);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      await adminService.uploadProductImage(supersedeNo, uom, file);
      message.success('Image uploaded');
      await load();
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (img: ProductImage) => {
    setBusyId(img.KeyID);
    try {
      await adminService.deleteProductImage(supersedeNo, uom, img.KeyID);
      message.success('Image deleted');
      await load();
    } catch (error) {
      message.error(handleApiError(error) || 'Failed to delete image');
    } finally {
      setBusyId(null);
    }
  };

  const handleMove = async (img: ProductImage, direction: 'up' | 'down') => {
    setBusyId(img.KeyID);
    try {
      await adminService.moveProductImage(supersedeNo, uom, img.KeyID, direction);
      await load();
    } catch (error) {
      message.error(handleApiError(error) || 'Failed to move image');
    } finally {
      setBusyId(null);
    }
  };

  const remaining = MAX_IMAGES - images.length;

  return (
    <div style={{ marginBottom: 16 }}>
      <Text strong>Product Images</Text>
      <div style={{ marginTop: 8 }}>
        <Text type="secondary">
          Up to {MAX_IMAGES} images per product. The first image is the main thumbnail customers see in listings.
        </Text>
      </div>

      {loading ? (
        <div style={{ marginTop: 12 }}>
          <Text type="secondary">Loading images…</Text>
        </div>
      ) : (
        <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
          {images.map((img, idx) => (
            <Col key={img.KeyID} xs={12} sm={8} md={6}>
              <div
                style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: 8,
                  padding: 8,
                  background: '#fafafa',
                  position: 'relative',
                }}
              >
                {idx === 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 4,
                      left: 4,
                      background: '#ffae00',
                      color: '#fff',
                      padding: '2px 6px',
                      borderRadius: 4,
                      fontSize: 11,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      zIndex: 1,
                    }}
                  >
                    <StarFilled /> Primary
                  </div>
                )}
                <Image
                  src={`${ProductService.getProductImageUrl(img.path)}?v=${cacheBuster}-${img.KeyID}`}
                  alt={`Image ${img.SeqNo}`}
                  width="100%"
                  height={120}
                  style={{ objectFit: 'cover', borderRadius: 4 }}
                  fallback="/images/product-placeholder.svg"
                  preview={{
                    src: `${ProductService.getProductImageUrl(img.path)}?v=${cacheBuster}-${img.KeyID}`,
                  }}
                />
                <Space style={{ marginTop: 6, justifyContent: 'space-between', width: '100%' }}>
                  <Space size={4}>
                    <Tooltip title="Move up">
                      <Button
                        size="small"
                        icon={<ArrowUpOutlined />}
                        disabled={idx === 0 || busyId === img.KeyID}
                        onClick={() => handleMove(img, 'up')}
                      />
                    </Tooltip>
                    <Tooltip title="Move down">
                      <Button
                        size="small"
                        icon={<ArrowDownOutlined />}
                        disabled={idx === images.length - 1 || busyId === img.KeyID}
                        onClick={() => handleMove(img, 'down')}
                      />
                    </Tooltip>
                  </Space>
                  <Tooltip title="Delete image">
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      loading={busyId === img.KeyID}
                      onClick={() => handleDelete(img)}
                    />
                  </Tooltip>
                </Space>
              </div>
            </Col>
          ))}
        </Row>
      )}

      <div style={{ marginTop: 12 }}>
        <Upload
          accept=".jpg,.jpeg,.png"
          beforeUpload={(file) => {
            const validationError = validateImageFile(file);
            if (validationError) {
              message.error(validationError);
              return Upload.LIST_IGNORE;
            }
            handleUpload(file);
            return false;
          }}
          showUploadList={false}
          disabled={uploading || remaining <= 0}
        >
          <Tooltip title={IMAGE_REQUIREMENTS_TEXT}>
            <Button icon={<UploadOutlined />} loading={uploading} disabled={remaining <= 0}>
              {remaining <= 0 ? `Limit reached (${MAX_IMAGES})` : `Add image (${remaining} slot${remaining === 1 ? '' : 's'} left)`}
            </Button>
          </Tooltip>
        </Upload>
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
          {IMAGE_REQUIREMENTS_TEXT} New uploads append to the end; use the arrows to reorder.
        </Text>
      </div>
    </div>
  );
};

interface MobileProductPhotoProps {
  supersedeNo: string;
  uom: string;
  /** For the phone preview and as the photo to frame (issue #142). */
  product: Pick<Product, 'Description' | 'UOM' | 'Price' | 'MainPictureFull' | 'MainPictureName'>;
}

/**
 * Optional mobile-specific product photo (issue #126).
 *
 * Some photos are framed for the desktop product card and lose their top and
 * bottom to the crop on a phone, where the card is much wider relative to its
 * image height. This lets an admin supply a second photo cropped for mobile.
 *
 * It sits outside the numbered gallery on purpose: it never shows up in the
 * customer-facing image gallery, it does not use one of the five image slots,
 * and there is at most one, so uploading again simply replaces it.
 *
 * Issue #142 added a phone-sized preview of the product card, and an editor to
 * frame the main photo for that card directly instead of cropping it elsewhere.
 */
const MobileProductPhoto: React.FC<MobileProductPhotoProps> = ({ supersedeNo, uom, product }) => {
  const [photo, setPhoto] = useState<{ DocName: string; path: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cropping, setCropping] = useState(false);
  const mainPhoto = product.MainPictureFull || product.MainPictureName;
  // The mobile photo keeps one filename across replacements, so without a cache
  // buster the browser would keep showing the photo that was just overwritten.
  const [cacheBuster, setCacheBuster] = useState<number>(() => Date.now());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setPhoto(await adminService.getMobileProductImage(supersedeNo, uom));
      setCacheBuster(Date.now());
    } catch (error) {
      message.error(handleApiError(error) || 'Failed to load the mobile photo');
    } finally {
      setLoading(false);
    }
  }, [supersedeNo, uom]);

  useEffect(() => {
    load();
  }, [load]);

  const handleUpload = async (file: File): Promise<boolean> => {
    setBusy(true);
    try {
      await adminService.uploadMobileProductImage(supersedeNo, uom, file);
      message.success(photo ? 'Mobile photo replaced' : 'Mobile photo uploaded');
      await load();
      return true;
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Failed to upload the mobile photo');
      return false;
    } finally {
      setBusy(false);
    }
  };

  // Framed in the editor and saved through the same upload as a chosen file.
  const handleCropSave = async (file: File) => {
    if (await handleUpload(file)) setCropping(false);
  };

  const previewUrl = photo
    ? `${ProductService.getProductImageUrl(photo.path)}?v=${cacheBuster}`
    : ProductService.getProductImageUrl(mainPhoto);

  const handleDelete = async () => {
    setBusy(true);
    try {
      await adminService.deleteMobileProductImage(supersedeNo, uom);
      message.success('Mobile photo removed');
      await load();
    } catch (error) {
      message.error(handleApiError(error) || 'Failed to remove the mobile photo');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <Text strong>Mobile Photo</Text>
      <div style={{ marginTop: 8 }}>
        <Text type="secondary">
          Optional. Used instead of the main photo on phone-sized screens, where the
          product card is wider and can crop a desktop photo. Leave this empty and
          the main photo is used everywhere.
        </Text>
      </div>

      {loading ? (
        <div style={{ marginTop: 12 }}>
          <Text type="secondary">Loading mobile photo…</Text>
        </div>
      ) : (
        <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
          <Col xs={12} sm={8} md={6}>
            <div
              style={{
                border: '1px solid #d9d9d9',
                borderRadius: 8,
                padding: 8,
                background: '#fafafa',
                minHeight: 120,
              }}
            >
              {photo ? (
                <>
                  <Image
                    src={`${ProductService.getProductImageUrl(photo.path)}?v=${cacheBuster}`}
                    alt="Mobile photo"
                    width="100%"
                    height={120}
                    style={{ objectFit: 'cover', borderRadius: 4 }}
                    fallback="/images/product-placeholder.svg"
                    preview={{
                      src: `${ProductService.getProductImageUrl(photo.path)}?v=${cacheBuster}`,
                    }}
                  />
                  <Space style={{ marginTop: 6, justifyContent: 'flex-end', width: '100%' }}>
                    <Tooltip title="Remove the mobile photo and fall back to the main photo">
                      <Button size="small" danger icon={<DeleteOutlined />} loading={busy} onClick={handleDelete} />
                    </Tooltip>
                  </Space>
                </>
              ) : (
                <div style={{ padding: '36px 8px', textAlign: 'center' }}>
                  <Text type="secondary">No mobile photo</Text>
                </div>
              )}
            </div>
          </Col>
          <Col xs={24} md={14}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              Preview on a phone{photo ? '' : ' (using the main photo)'}:
            </Text>
            <MobileCardPreview
              imageUrl={previewUrl}
              description={product.Description}
              uom={product.UOM}
              // The admin listing's PriceDesc1 is not the storefront's price
              // label, so the price is formatted the way the card shows it.
              price={`$ ${Number(product.Price ?? 0).toFixed(2)}`}
            />
          </Col>
        </Row>
      )}

      <MobilePhotoCropper
        open={cropping}
        sourceUrl={ProductService.getProductImageUrl(mainPhoto)}
        saving={busy}
        onCancel={() => setCropping(false)}
        onSave={handleCropSave}
      />

      <div style={{ marginTop: 12 }}>
        <Space wrap style={{ marginRight: 8 }}>
          <Tooltip
            title={
              mainPhoto
                ? 'Drag and zoom the main photo inside the phone card frame, then save it as the mobile photo'
                : 'Add a main photo first'
            }
          >
            <Button icon={<ScissorOutlined />} onClick={() => setCropping(true)} disabled={busy || !mainPhoto}>
              Adjust main photo for mobile
            </Button>
          </Tooltip>
        </Space>
        <Upload
          accept=".jpg,.jpeg,.png"
          beforeUpload={(file) => {
            const validationError = validateImageFile(file);
            if (validationError) {
              message.error(validationError);
              return Upload.LIST_IGNORE;
            }
            handleUpload(file);
            return false;
          }}
          showUploadList={false}
          disabled={busy}
        >
          <Tooltip title={MOBILE_IMAGE_REQUIREMENTS_TEXT}>
            <Button icon={<UploadOutlined />} loading={busy}>
              {photo ? 'Replace mobile photo' : 'Add mobile photo'}
            </Button>
          </Tooltip>
        </Upload>
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
          {MOBILE_IMAGE_REQUIREMENTS_TEXT}
        </Text>
      </div>
    </div>
  );
};

export default ProductManagement;
